/**
 * Documents Service API
 * Document lifecycle registry and governance endpoints
 */

import { createAPIClient } from '../client'
import { API_CONFIG } from '../config'

const client = createAPIClient(API_CONFIG.DOCUMENTS.baseURL, API_CONFIG.DOCUMENTS.timeout)

export const documentsService = {
  // ==================== Documents ====================

  /**
   * Create a new document record (starts in DRAFT status)
   * @param {object} data - { project_id, document_type, title, description }
   * @returns {Promise} Created document
   */
  createDocument: async (data) => {
    const response = await client.post('/documents', data)
    return response.data
  },

  /**
   * Get full document details with versions and audit events
   * @param {string} id - Document UUID
   * @returns {Promise} Document with versions[] and events[]
   */
  getDocument: async (id) => {
    const response = await client.get(`/documents/${id}`)
    return response.data
  },

  /**
   * Search documents with filters
   * @param {object} params - { project_id, status, document_type, date_from, date_to, title_search, page, page_size }
   * @returns {Promise} { total, page, page_size, results[] }
   */
  searchDocuments: async (params = {}) => {
    const response = await client.get('/documents/search', { params })
    return response.data
  },

  // ==================== Versions ====================

  /**
   * Attach a new file version to a document
   * @param {string} documentId - Document UUID
   * @param {object} data - { file_name, file_path, checksum }
   * @returns {Promise} Created version
   */
  addVersion: async (documentId, data) => {
    const response = await client.post(`/documents/${documentId}/versions`, data)
    return response.data
  },

  // ==================== Lifecycle Transitions ====================

  /**
   * Issue a document (DRAFT → ISSUED)
   * @param {string} id - Document UUID
   * @returns {Promise} Updated document
   */
  issueDocument: async (id) => {
    const response = await client.post(`/documents/${id}/issue`)
    return response.data
  },

  /**
   * Mark document as awaiting signature (ISSUED → AWAITING_SIGNATURE)
   * @param {string} id - Document UUID
   * @returns {Promise} Updated document
   */
  markAwaitingSignature: async (id) => {
    const response = await client.post(`/documents/${id}/mark-awaiting-signature`)
    return response.data
  },

  /**
   * Register executed copy with file version (AWAITING_SIGNATURE → EXECUTED)
   * @param {string} id - Document UUID
   * @param {object} data - { file_name, file_path, checksum }
   * @returns {Promise} Updated document
   */
  registerExecuted: async (id, data) => {
    const response = await client.post(`/documents/${id}/register-executed`, data)
    return response.data
  },

  /**
   * Close a document (EXECUTED → CLOSED)
   * @param {string} id - Document UUID
   * @returns {Promise} Updated document
   */
  closeDocument: async (id) => {
    const response = await client.post(`/documents/${id}/close`)
    return response.data
  },

  /**
   * Void a document from any non-terminal state
   * @param {string} id - Document UUID
   * @param {string} [reason] - Reason for voiding
   * @returns {Promise} Updated document
   */
  voidDocument: async (id, reason = null) => {
    const params = reason ? { reason } : {}
    const response = await client.post(`/documents/${id}/void`, null, { params })
    return response.data
  },

  // ==================== Cross-Module Registration ====================

  /**
   * Register a document from an external module (idempotent)
   * @param {object} data - { source_module, source_id, source_url, document_type, title, ... }
   * @returns {Promise} Registered document
   */
  registerDocument: async (data) => {
    const response = await client.post('/documents/register', data)
    return response.data
  },

  // ==================== Federated Discovery ====================

  /**
   * Discover documents across all BFS modules (Bid Workup, CRM, etc.)
   * @param {object} params - { modules, title_search }
   * @returns {Promise} { total, results[] } with ExternalDocumentSummary items
   */
  federatedSearch: async (params = {}) => {
    const response = await client.get('/documents/federated', { params })
    return response.data
  },

  // ==================== Health ====================

  /**
   * Check service health
   * @returns {Promise} { status, service, version, database }
   */
  healthCheck: async () => {
    // Health is at root, not under /api/v1
    const baseClient = createAPIClient(
      API_CONFIG.DOCUMENTS.baseURL.replace('/api/v1', ''),
      API_CONFIG.DOCUMENTS.timeout
    )
    const response = await baseClient.get('/health')
    return response.data
  },
}
